/**
 * Navigation Guard Context (ORCH-393)
 *
 * Purpose: One "Leave without saving?" guard for every full-page editor. A page
 *          with unsaved work arms the guard; whatever would navigate away from
 *          it asks first, and the person can Stay or Discard.
 * Logic: The armed page is the single source of truth — it registers while its
 *        own dirty flag is true and clears on unmount, so only one guard can be
 *        armed at a time. Navigation sources don't know which page is open: they
 *        hand their navigation to `confirmNavigation`, which runs it straight
 *        away when nothing is armed and otherwise parks it behind the dialog.
 *        While a guard is armed a `beforeunload` listener also hands closing or
 *        reloading the tab to the browser's own prompt, and a duplicate history
 *        entry (the "trap") makes the browser's Back button land on the same
 *        page first, so it can ask too — see `useHistoryTrap`.
 *        Outside a provider the guard is inert and navigation is never
 *        interrupted, so a page can be unit-tested without the app shell.
 * Dependencies: ConfirmDialog
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Typography } from '@mui/material';
import ConfirmDialog from '../components/common/ConfirmDialog';

const DEFAULT_TITLE = 'Leave without saving?';
const DEFAULT_MESSAGE = 'Your unsaved changes will be lost.';

/** What a page tells the guard about its unsaved work. */
export interface UnsavedChangesGuard {
  /** Arm the guard while this is true. */
  when: boolean;
  /** Dialog title. Defaults to "Leave without saving?" */
  title?: string;
  /** What is lost, in a sentence. */
  message?: string;
  /**
   * Put the page back to its stored state. Called when the person chooses
   * Discard, before the navigation runs, so a navigation that leaves the page
   * mounted (a tab or query-string change) doesn't come back still dirty.
   */
  onDiscard?: () => void;
}

type ArmedGuard = Omit<UnsavedChangesGuard, 'when'>;

export interface NavigationGuardValue {
  /** Whether a page currently has unsaved changes. */
  hasUnsavedChanges: boolean;
  /** Run `proceed` now when nothing is armed; otherwise ask first. */
  confirmNavigation: (proceed: () => void) => void;
  /** Internal — pages arm the guard through `useUnsavedChangesGuard`. */
  register: (guard: ArmedGuard | null) => void;
}

const inert: NavigationGuardValue = {
  hasUnsavedChanges: false,
  confirmNavigation: (proceed) => proceed(),
  register: () => undefined,
};

const NavigationGuardContext = createContext<NavigationGuardValue>(inert);

/** Marks the duplicate history entry the trap pushes, so it can be told apart from a real one. */
export const HISTORY_TRAP_KEY = '__navigationGuardTrap';

/** Push a copy of the current history entry, marked as the trap. */
const pushTrap = () => {
  const { history, location } = window;
  // React Router keeps its own `usr` / `key` / `idx` in the state: copying them
  // means the trap resolves to the same location, so popping to it is a no-op
  history.pushState({ ...(history.state ?? {}), [HISTORY_TRAP_KEY]: true }, '', location.href);
};

const trapIsCurrent = () => !!window.history.state?.[HISTORY_TRAP_KEY];

/**
 * Catch the browser's Back button while a guard is armed.
 *
 * Purpose: `<BrowserRouter>` offers no way to block a history pop — by the time
 *          `popstate` fires the router has already moved and the page is
 *          unmounting. The only interception point is to make Back land
 *          somewhere harmless first.
 * Logic: While armed, the current entry is duplicated (the "trap"), so Back
 *        pops onto the same page: the router sees the same location, the page
 *        stays mounted, and the pop is handed to `onPop`, which re-pushes the
 *        trap and asks. Discard then walks back past both entries. Disarming
 *        while the trap is still the current entry pops it again, so a saved
 *        page is left with the history it started with. Pops the guard itself
 *        requested are run through `bypass` and never reach `onPop`.
 * Input: whether a guard is armed; what to do with a pop the person made.
 * Output: `leaveTrap` — pop the trap, then run a navigation; `go` — walk back
 *         past the re-pushed trap and the page.
 */
const useHistoryTrap = (armed: boolean, onPop: () => void) => {
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;
  // What to run when the pop the guard itself asked for lands
  const bypassRef = useRef<(() => void) | null>(null);
  // Whether the guard has a trap on the stack, or is in the middle of popping it
  const trapRef = useRef<'none' | 'set' | 'popping'>('none');

  // One listener for the hook's lifetime: a pop the guard asked for while
  // disarming lands after the armed window has closed, and still has to be
  // recognised as the guard's own
  useEffect(() => {
    const handlePop = () => {
      const bypass = bypassRef.current;
      if (bypass) {
        bypassRef.current = null;
        // Re-armed before the pop landed: the new trap is still on the stack
        if (trapRef.current === 'popping') trapRef.current = 'none';
        bypass();
        return;
      }
      if (trapRef.current !== 'set') return;
      // The person pressed Back: the trap just popped onto the page itself.
      // Put it back so a second press is caught the same way, then ask.
      pushTrap();
      onPopRef.current();
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  useEffect(() => {
    if (!armed) return undefined;
    pushTrap();
    trapRef.current = 'set';
    return () => {
      if (trapRef.current !== 'set') return;
      // Disarmed on the page (saved, or its own Discard): take the trap away
      // again. After a navigation the trap is no longer current and stays
      // harmlessly behind the new entry.
      if (trapIsCurrent()) {
        trapRef.current = 'popping';
        bypassRef.current = () => undefined;
        window.history.back();
      } else {
        trapRef.current = 'none';
      }
    };
  }, [armed]);

  /** Pop the trap first, then run `proceed`, so the entry it pushes replaces the trap. */
  const leaveTrap = useCallback((proceed: () => void) => {
    if (trapRef.current === 'set' && trapIsCurrent()) {
      trapRef.current = 'popping';
      bypassRef.current = proceed;
      window.history.back();
      return;
    }
    proceed();
  }, []);

  /** Walk back past the re-pushed trap and the page, to where Back was going. */
  const goBack = useCallback(() => {
    trapRef.current = 'popping';
    bypassRef.current = () => undefined;
    window.history.go(-2);
  }, []);

  return { leaveTrap, goBack };
};

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [armed, setArmed] = useState(false);
  // The guard is captured with the navigation: the dialog describes what was
  // unsaved when the person clicked, even if the page has re-rendered since
  const [pending, setPending] = useState<{ proceed: () => void; guard: ArmedGuard } | null>(null);
  // The ref, not the state, is what a navigation source reads. It is written
  // synchronously by `register` so arming and disarming take effect the moment
  // the page says so, without waiting for a render to flush: a click landing in
  // that window would otherwise be judged against the previous state. The state
  // copy only drives `beforeunload`, the history trap and `hasUnsavedChanges`.
  const guardRef = useRef<ArmedGuard | null>(null);

  const register = useCallback((next: ArmedGuard | null) => {
    guardRef.current = next;
    setArmed(!!next);
  }, []);

  const confirmNavigation = useCallback((proceed: () => void) => {
    const armedGuard = guardRef.current;
    if (!armedGuard) {
      proceed();
      return;
    }
    setPending({ proceed, guard: armedGuard });
  }, []);

  // The browser's Back button: the trap hands the pop here, and Discard goes back
  const { leaveTrap, goBack } = useHistoryTrap(armed, () => {
    const armedGuard = guardRef.current;
    if (!armedGuard) return;
    setPending({ proceed: goBack, guard: armedGuard });
  });

  // Closing or reloading the tab gets the browser's own prompt, not ours
  useEffect(() => {
    if (!armed) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome only shows the prompt when returnValue is set; the wording is the browser's
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [armed]);

  const value = useMemo<NavigationGuardValue>(
    () => ({ hasUnsavedChanges: armed, confirmNavigation, register }),
    [armed, confirmNavigation, register]
  );

  // Discarding resets the page, which clears its own guard on the next render
  const discard = () => {
    if (!pending) return;
    const { proceed, guard: armedGuard } = pending;
    setPending(null);
    armedGuard.onDiscard?.();
    // A Back press already re-pushed the trap and goes back through it; an
    // in-app navigation pops the trap first so its new entry replaces it
    if (proceed === goBack) {
      goBack();
    } else {
      leaveTrap(proceed);
    }
  };

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        title={pending?.guard.title || DEFAULT_TITLE}
        confirmLabel="Discard changes"
        cancelLabel="Stay"
        confirmColor="error"
        onConfirm={discard}
        onClose={() => setPending(null)}
        dataTestId="leave-guard-dialog"
      >
        <Typography variant="body2">{pending?.guard.message || DEFAULT_MESSAGE}</Typography>
      </ConfirmDialog>
    </NavigationGuardContext.Provider>
  );
};

/**
 * Ask before navigating away from a page with unsaved changes.
 *
 * Use in anything that navigates out of the current page — nav items,
 * breadcrumbs, a Back button, a tenant switch:
 *   const { confirmNavigation } = useNavigationGuard();
 *   confirmNavigation(() => navigate('/admin'));
 */
export const useNavigationGuard = (): NavigationGuardValue => useContext(NavigationGuardContext);

/**
 * Arm the guard from a page that drafts changes before saving.
 *
 * Purpose: Register the page's unsaved state so every navigation source asks
 *          before leaving, and the browser prompts on close or reload.
 * Logic: Registers while `when` is true and clears on unmount or when the page
 *        goes clean. `onDiscard` is read through a ref, so callers don't have to
 *        memoize it.
 * Input: the page's dirty flag plus the wording and reset for the dialog.
 * Output: None (side effect: the guard is armed).
 */
export const useUnsavedChangesGuard = ({
  when,
  title,
  message,
  onDiscard,
}: UnsavedChangesGuard): void => {
  const { register } = useNavigationGuard();
  const discardRef = useRef(onDiscard);
  discardRef.current = onDiscard;

  useEffect(() => {
    if (!when) {
      register(null);
      return undefined;
    }
    register({ title, message, onDiscard: () => discardRef.current?.() });
    return () => register(null);
  }, [when, title, message, register]);
};

export default NavigationGuardContext;
